import React, { useContext, useState, useEffect } from 'react';
import { Grid, Menu, MenuItem, IconButton, CircularProgress } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import ExportIdentifiedSpeciesMenuItem from './ExportIdentifiedSpeciesMenuItem';
import ExportKgASTPredictionsMenuItem from './ExportKgASTPredictionsMenuItem';
import { IdentifiedSpeciesFiltersContext } from '../context/IdentifiedSpeciesFiltersContext';
import { useSummarizeAnalysis } from '../services/api/KeynomeAPIMutations';
import { useQueryClient } from '@tanstack/react-query';

function IdentifiedSpeciesMenu(props) {
    const {
        loading,
        isIllumina,
        run,
        sample,
        analysis,
        setIsResistanceGeneDialogOpen,
        hasAccessExtendedResistanceGenePanel,
        hasAccessResultsSummary,
        setIsSummaryDialogOpen
    } = props;

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const { loadingResistanceGenes } = useContext(IdentifiedSpeciesFiltersContext);
    const [ isExporting, setIsExporting ] = useState(false);
    const [ hasSummary, setHasSummary ] = useState(false);

    const queryClient = useQueryClient();
    const { mutateAsync: summarizeAnalysis, isPending: summarizingAnalysis } = useSummarizeAnalysis();

    useEffect(() => {
        if (!hasSummary && analysis?.attributes?.organism_id_summary && analysis?.attributes?.amr_prediction_summary) {
            setHasSummary(true);
        }
    }, [hasSummary, analysis])

    const open = Boolean(menuAnchorEl);
    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };
      
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleSummaryClick = async () => {
        if (hasSummary) {
            handleMenuClose();
            setIsSummaryDialogOpen(true);
        } else {
            await summarizeAnalysis({ analysisId: analysis?.id });
            setHasSummary(true);
            
            // Invalidate queries for this analysis to refetch data with summaries in all components
            await queryClient.invalidateQueries({
                predicate: ({ queryKey }) => queryKey[0].startsWith(`v1/lib_seqs/${sample.id}/analyses?primary_only=True`),
                refetchType: 'all',
                refetchInactive: true,
            });
        }
    };

    return (
        <Grid item sx={{textAlign: 'right'}}>
            <IconButton size="small" onClick={handleMenuClick}>
                <MoreVertIcon />
            </IconButton>
            <Menu
                anchorEl={menuAnchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {hasAccessExtendedResistanceGenePanel &&
                    <MenuItem disabled={loading || loadingResistanceGenes} onClick={() => {
                        setIsResistanceGeneDialogOpen(true);
                        handleMenuClose();
                    }}>
                        Complete Resistance Gene Profile
                        {loadingResistanceGenes && (
                            <CircularProgress size={16} sx={{ ml: 1 }} />
                        )}
                    </MenuItem>
                }
                <ExportIdentifiedSpeciesMenuItem
                    isIllumina={isIllumina}
                    isDatasetLevel={false}
                    run={run}
                    samples={[sample]}
                    handleMenuClose={handleMenuClose}
                    oneMenuItemClicked={isExporting}
                    setOneMenuItemClicked={setIsExporting}
                />
                <ExportKgASTPredictionsMenuItem
                    isIllumina={isIllumina}
                    isDatasetLevel={false}
                    run={run}
                    samples={[sample]}
                    handleMenuClose={handleMenuClose}
                    oneMenuItemClicked={isExporting}
                    setOneMenuItemClicked={setIsExporting}
                />
                {hasAccessResultsSummary && (
                <MenuItem
                    onClick={handleSummaryClick}
                    disabled={!analysis || summarizingAnalysis}
                    sx={{
                        color: '#9c27b0',
                        '&.Mui-disabled': {
                            color: '#9c27b0'
                        }
                    }}
                >
                    {summarizingAnalysis ? (
                        <CircularProgress size={16} sx={{ mr: 1, color: '#9c27b0' }} />
                    ) : (
                        <AutoAwesomeIcon sx={{ mr: 1 }} />
                    )}
                    {hasSummary ? 'View Results Summary' : 'Generate Results Summary'}
                </MenuItem>
                )}
            </Menu>
        </Grid>
    );
}

export default IdentifiedSpeciesMenu;